import { useEffect, useRef, useCallback, useState } from 'react';

/**
 * Lock body scroll when modal is open
 */
export function useBodyScrollLock(isOpen) {
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);
}

/**
 * Close on Escape key
 */
export function useEscapeKey(callback, isOpen, { disabled = false } = {}) {
  useEffect(() => {
    if (!isOpen || disabled) return;
    const handler = (e) => {
      if (e.key === 'Escape') callback();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, callback, disabled]);
}

/**
 * Trap focus within a container
 */
export function useFocusTrap(containerRef, isOpen) {
  const previousFocusRef = useRef(null);

  useEffect(() => {
    if (!isOpen || !containerRef.current) return;
    previousFocusRef.current = document.activeElement;

    const selector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const focusable = containerRef.current.querySelectorAll(selector);
    if (focusable.length > 0) {
      focusable[0].focus();
    }

    const trap = (e) => {
      if (e.key !== 'Tab' || !containerRef.current) return;
      const els = containerRef.current.querySelectorAll(selector);
      if (els.length === 0) return;
      const first = els[0];
      const last = els[els.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', trap);

    return () => {
      document.removeEventListener('keydown', trap);
      if (previousFocusRef.current?.focus) {
        previousFocusRef.current.focus();
      }
    };
  }, [isOpen, containerRef]);
}

/**
 * Detect scroll position to show/hide header and footer shadows
 */
export function useScrollShadow(scrollRef) {
  const [headerShadow, setHeaderShadow] = useState(false);
  const [footerShadow, setFooterShadow] = useState(false);

  const update = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setHeaderShadow(el.scrollTop > 2);
    setFooterShadow(el.scrollHeight - el.scrollTop - el.clientHeight > 2);
  }, [scrollRef]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    // Initial check
    update();
    el.addEventListener('scroll', update, { passive: true });
    // Also check on resize / content change
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, [scrollRef, update]);

  return { headerShadow, footerShadow };
}
